import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, map, Observable, startWith } from 'rxjs';
import { NavService } from '../nav/nav.service';
import { NotificationService } from '../../utilities/PortalNotificationUtility';
import { OrganizationService } from '../../feature/organizations/services/organizations.service';
import { Logger } from '../../infrastructure/logging/logger';
import { SearchService } from '../../shared/search/search.service';

@Component({
  selector: 'app-organization-search',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatInputModule,
    CommonModule
  ],
  templateUrl: './organization-search.component.html',
  styleUrl: './organization-search.component.scss'
})
export class OrganizationSearchComponent implements OnInit, AfterViewInit {

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  loading = new BehaviorSubject<boolean>(false);
  selectedOrgId = new BehaviorSubject<number | undefined>(undefined);
  keyword!: string;
  orgControl = new FormControl('');
  organizations: any[] = [];
  filteredOrganizations!: Observable<any[]>;

  constructor(
    private readonly notificationService: NotificationService,
    private navService: NavService,
    private organizationService: OrganizationService,
    private router: Router,
    private logService: Logger,
    private searchService: SearchService
  ) { }

  ngOnInit() {
    this.loadOrganizations();
  }

  ngAfterViewInit() {
    this.autocompleteTrigger.panelClosingActions.subscribe((e) => {      
      if (!(e && e.source)) {
        this.orgControl.setValue('');
        this.autocompleteTrigger.closePanel();
      }
    });

    this.navService.selectedOrg$.subscribe((org: any) => {
      if (org) {
        this.orgControl.setValue(org.organization_name);
      }
    });
  }

  async loadOrganizations() {
    try {
      this.loading.next(true);

      const organizations = await this.organizationService.getOrganizations();
      this.organizations = organizations;
      this.filteredOrganizations = this.orgControl.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || '')),
      );
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Organizations search response', 'Error while retrieving Organizations search response');
    } finally {
      this.loading.next(false);
    }
  }

  private _filter(value: string): string[] {
    if (value.length > 0) {
      return this.organizations.filter(org => org.organization_name.toLowerCase().includes(value.toLowerCase()));
    } else {
      return this.organizations;
    }
  }

  onOrganizationSelectEvent(event: MatAutocompleteSelectedEvent) {
    let selectedOrg = event.option.value;
    this.orgControl.setValue(selectedOrg.organization_name);
    this.navService.setSelectedOrganization(selectedOrg);

    let currentPath = this.router.url.split("/")[1];
    if (currentPath === 'properties' || currentPath === 'guests' || currentPath === 'reservations' || currentPath === 'claims') {
      this.router.navigate([currentPath]);
    }

    this.notificationService.showNotification({ message: `Added '${selectedOrg.organization_name}' Filter`});
  }

  clearSearch(searchKeyword: any) {
    searchKeyword.value = '';
    this.keyword = '';
    this.searchService.removeOrganizationId();
    this.loadOrganizations();
    this.navService.removeSelectedOrganization();
    this.notificationService.showNotification({ message: `Removed '${ this.orgControl.value }' Filter`});
  }
}